import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import {
  ClipboardList, Camera, Search, CheckCircle, AlertTriangle,
  RefreshCw, X, Loader, MapPin, ChevronDown, ChevronRight, History, Trash2, Eye,
} from 'lucide-react';

// The scanner (and the ~200KB @zxing bundle behind it) is a separate chunk,
// fetched the first time someone taps "Scan Device".
const RoundupScanner = React.lazy(() => import('./RoundupScanner'));

// Pool keys are opaque: the server holds the actual SQL filter behind them
// and the client never sends a fragment. The list comes from
// GET /api/roundups/pools, which is derived from ROUNDUP_POOLS server-side,
// so a new pool needs no edit here.
//
// SCD stays the default selection because it is the older, more frequent
// walk-through. NOTE: the picker does not auto-resume — if a roundup is open
// for a pool that is not selected, you have to tap that pool to see it.
const DEFAULT_POOL_KEY = 'scd-acs900';

// Above this many home-department groups the list is long enough that
// scrolling it end to end stops being useful, so groups start collapsed.
// SCD has 5 groups and keeps its existing always-expanded behaviour;
// Alaris has 17 and starts collapsed.
const COLLAPSE_GROUPS_ABOVE = 8;

const authHeaders = () => {
  const token = localStorage.getItem('pmplus_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...authHeaders() });

const pendingStorageKey = (roundupId) => `roundup_pending_${roundupId}`;

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Hours up to two days, then whole days — a walk-through is either an
// afternoon or it dragged across a week, and nothing in between matters.
const formatDuration = (start, end) => {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const hours = ms / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} h`;
  return `${Math.round(hours / 24)} d`;
};

// Rows created before the name field became required have no name; show
// something a person can still tell apart.
const displayName = (row) =>
  (row.name && row.name.trim()) || `${row.pool_label} — ${formatDate(row.started_at)}`;

const complianceColor = (pct) => {
  if (pct >= 90) return { color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' };
  if (pct >= 70) return { color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
  return { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' };
};

const RoundupTracker = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pool, setPool] = useState(null);
  const [poolKey, setPoolKey] = useState(DEFAULT_POOL_KEY);
  const [pools, setPools] = useState([]);
  const [roundup, setRoundup] = useState(null);
  const [devices, setDevices] = useState([]);
  const [counters, setCounters] = useState({ total: 0, found: 0, drifted: 0, missing: 0 });
  const [departments, setDepartments] = useState([]);

  // 'all' | 'missing' | 'drifted' | 'found'
  const [statusFilter, setStatusFilter] = useState('all');
  // Department name -> true when the operator has collapsed it. Seeded from
  // the group count the first time an active roundup's devices land.
  const [collapsed, setCollapsed] = useState({});

  // 'pools' (picker + landing/active roundup) | 'history'
  const [tab, setTab] = useState('pools');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyPool, setHistoryPool] = useState('');   // '' = all pools
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Name is required client-side from 2026-08-28 — empty names produced
  // history rows that could only be told apart by their start date.
  const [nameError, setNameError] = useState('');

  const [starting, setStarting] = useState(false);
  const [newName, setNewName] = useState('');

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMessage, setScanMessage] = useState('');
  const [manualQuery, setManualQuery] = useState('');

  const [modalDevice, setModalDevice] = useState(null);
  const [modalDept, setModalDept] = useState('');
  const [modalOtherDept, setModalOtherDept] = useState('');
  const [modalNotes, setModalNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  // Saves that failed to reach the server (dead Wi-Fi in the building).
  // Held in state AND localStorage so a Safari tab reload mid-walk doesn't
  // lose them.
  const [pending, setPending] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const loadPending = useCallback((roundupId) => {
    try {
      const raw = localStorage.getItem(pendingStorageKey(roundupId));
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }, []);

  const persistPending = useCallback((roundupId, list) => {
    try {
      if (list.length === 0) localStorage.removeItem(pendingStorageKey(roundupId));
      else localStorage.setItem(pendingStorageKey(roundupId), JSON.stringify(list));
    } catch (_) {
      // storage full or blocked — the in-memory queue still works
    }
  }, []);

  // Big pools open collapsed; small ones (SCD) stay expanded as before.
  // Keyed on the pool so that a mid-walk Refresh does NOT slam shut every
  // group the operator has opened — only a genuine pool switch re-seeds.
  const seededFor = useRef(null);
  const seedCollapsed = useCallback((deviceList, key) => {
    if (seededFor.current === key) return;
    seededFor.current = key;
    const depts = Array.from(
      new Set((deviceList || []).map((d) => d.home_department || 'Unassigned'))
    );
    if (depts.length <= COLLAPSE_GROUPS_ABOVE) return setCollapsed({});
    const next = {};
    depts.forEach((d) => { next[d] = true; });
    setCollapsed(next);
  }, []);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/roundups/current?pool=${encodeURIComponent(poolKey)}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setPool(data.pool);
      setRoundup(data.roundup);
      setDevices(data.devices || []);
      setCounters(data.counters || { total: 0, found: 0, drifted: 0, missing: 0 });
      seedCollapsed(data.devices || [], poolKey);
      if (data.roundup) setPending(loadPending(data.roundup.id));
      else setPending([]);
    } catch (err) {
      setError(`Could not load the roundup: ${err.message}`);
    }
    setLoading(false);
  }, [loadPending, poolKey, seedCollapsed]);

  const loadPools = useCallback(async () => {
    try {
      const res = await fetch('/api/roundups/pools', { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setPools(data.pools || []);
    } catch (_) {
      // Non-fatal: without the list the picker hides and the default pool
      // still loads, which is the pre-picker behaviour.
    }
  }, []);

  const loadHistory = useCallback(async (poolFilter) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const qs = poolFilter ? `?pool=${encodeURIComponent(poolFilter)}` : '';
      const res = await fetch(`/api/roundups/history${qs}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setHistory(data.roundups || []);
    } catch (err) {
      setHistoryError(`Could not load history: ${err.message}`);
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  const openDetail = async (row) => {
    setDetail({ roundup: { ...row }, departments: null });
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/roundups/${row.id}/summary`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setDetail(data);
    } catch (err) {
      setDetail({ roundup: { ...row }, departments: [], error: err.message });
    }
    setDetailLoading(false);
  };

  const deleteRoundup = async (row) => {
    const when = formatDate(row.started_at);
    const label = displayName(row);
    if (!window.confirm(
      `Delete ${label}? Started ${when}, ${row.compliance_pct}%. ` +
      'This will hide it from the dashboard but preserve data.'
    )) return;

    setDeletingId(row.id);
    try {
      const res = await fetch(`/api/roundups/${row.id}/delete`, {
        method: 'POST',
        headers: jsonHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setHistory((prev) => prev.filter((r) => r.id !== row.id));
      // Deleting the roundup that is currently open on the Pools tab has to
      // clear it there too, or the active screen keeps writing movements to
      // a roundup the server now hides.
      if (roundup && roundup.id === row.id) await loadCurrent();
    } catch (err) {
      setHistoryError(`Could not delete: ${err.message}`);
    }
    setDeletingId(null);
  };

  const loadDepartments = useCallback(async () => {
    try {
      const res = await fetch('/api/roundups/departments', { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setDepartments(data.departments || []);
    } catch (_) {
      // Non-fatal: the 5 home departments are always available as fast taps.
    }
  }, []);

  // loadCurrent is keyed on poolKey, so switching pools re-runs this.
  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    loadDepartments();
    loadPools();
  }, [loadDepartments, loadPools]);

  useEffect(() => {
    if (tab === 'history') loadHistory(historyPool);
  }, [tab, historyPool, loadHistory]);

  const startRoundup = async () => {
    if (!newName.trim()) {
      setNameError('Give the roundup a name — it is how you will find it in History later.');
      return;
    }
    setNameError('');
    setStarting(true);
    setError('');
    try {
      const res = await fetch('/api/roundups', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ pool: poolKey, name: newName.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setPool(data.pool);
      setRoundup(data.roundup);
      setDevices(data.devices || []);
      setCounters(data.counters);
      setStatusFilter('all');
      seedCollapsed(data.devices || [], poolKey);
      setPending([]);
      setNewName('');
    } catch (err) {
      setError(`Could not start the roundup: ${err.message}`);
    }
    setStarting(false);
  };

  // Scanned text -> device. Per Gene: the barcode carries NatchInv.name (the
  // 7-digit asset #), so that's checked first; asset and serial are a safety
  // net for oddball tags.
  const resolveScan = (raw) => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const t = norm(raw);
    if (!t) return null;
    return (
      devices.find((d) => norm(d.name) === t) ||
      devices.find((d) => norm(d.asset) === t) ||
      devices.find((d) => norm(d.serial) === t) ||
      devices.find((d) => norm(d.name) && t.includes(norm(d.name))) ||
      null
    );
  };

  const openSaveModal = (device) => {
    setModalDevice(device);
    setModalDept(device.found_at_department || device.home_department || '');
    setModalOtherDept('');
    setModalNotes('');
    setModalError('');
  };

  const handleDetected = (text) => {
    setScannerOpen(false);
    const match = resolveScan(text);
    if (match) {
      setScanMessage('');
      openSaveModal(match);
    } else {
      setScanMessage(`Scanned "${String(text).trim()}" — no device in this pool matches. Try manual entry.`);
    }
  };

  // Optimistically reflect a save that hasn't reached the server yet.
  const applyLocal = (inventoryId, foundAt, returnedTo, isPending) => {
    setDevices((prev) => {
      const next = prev.map((d) =>
        d.inventory_id === inventoryId
          ? {
              ...d,
              found: true,
              drifted: foundAt !== d.home_department,
              found_at_department: foundAt,
              returned_to_department: returnedTo || d.home_department,
              pendingSave: !!isPending,
            }
          : d
      );
      const found = next.filter((d) => d.found).length;
      setCounters({
        total: next.length,
        found,
        drifted: next.filter((d) => d.drifted).length,
        missing: next.length - found,
      });
      return next;
    });
  };

  const queueSave = (payload) => {
    setPending((prev) => {
      const next = [...prev.filter((p) => p.inventory_id !== payload.inventory_id), payload];
      if (roundup) persistPending(roundup.id, next);
      return next;
    });
  };

  const saveMovement = async () => {
    if (!modalDevice || !roundup) return;
    const dept = (modalDept === '__other__' ? modalOtherDept : modalDept).trim();
    if (!dept) {
      setModalError('Pick where the device was found.');
      return;
    }

    setSaving(true);
    setModalError('');
    const payload = {
      inventory_id: modalDevice.inventory_id,
      found_at_department: dept,
      notes: modalNotes.trim() || null,
      device_label: modalDevice.name,
    };

    try {
      const res = await fetch(`/api/roundups/${roundup.id}/movements`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        // 4xx is a real rejection (bad device, roundup closed) — queueing it
        // would just retry a guaranteed failure. 5xx might be transient.
        if (res.status >= 400 && res.status < 500) throw new Error(data.error || 'rejected');
        throw Object.assign(new Error(data.error || 'server error'), { retryable: true });
      }
      applyLocal(payload.inventory_id, dept, data.device ? data.device.returned_to_department : null, false);
      if (data.counters) setCounters(data.counters);
      setModalDevice(null);
    } catch (err) {
      // Network failure (fetch throws) or a 5xx: keep it and let them retry.
      if (err.retryable || err instanceof TypeError) {
        queueSave(payload);
        applyLocal(payload.inventory_id, dept, null, true);
        setModalDevice(null);
      } else {
        setModalError(err.message);
      }
    }
    setSaving(false);
  };

  const retryPending = async () => {
    if (!roundup || pending.length === 0) return;
    setRetrying(true);
    const stillPending = [];
    for (const item of pending) {
      try {
        const res = await fetch(`/api/roundups/${roundup.id}/movements`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify(item),
        });
        if (res.ok) {
          const data = await res.json();
          applyLocal(item.inventory_id, item.found_at_department, null, false);
          if (data.counters) setCounters(data.counters);
        } else if (res.status >= 500) {
          stillPending.push(item);
        }
        // 4xx: drop it, retrying won't help.
      } catch (_) {
        stillPending.push(item);
      }
    }
    setPending(stillPending);
    persistPending(roundup.id, stillPending);
    setRetrying(false);
  };

  const completeRoundup = async () => {
    if (!roundup) return;
    setCompleting(true);
    try {
      const res = await fetch(`/api/roundups/${roundup.id}/complete`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ notes: completeNotes.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      persistPending(roundup.id, []);
      setPending([]);
      setCompleteOpen(false);
      setCompleteNotes('');
      await loadCurrent();
    } catch (err) {
      setError(`Could not complete the roundup: ${err.message}`);
      setCompleteOpen(false);
    }
    setCompleting(false);
  };

  // ---- rendering helpers -------------------------------------------------

  // The pool's PINNED departments — the quick-tap chips in the save modal
  // and the leading groups in the list. For SCD these happen to be all five
  // home departments; for Alaris they are 5 of 17. Never use this as a
  // count of the pool's home departments (see departmentCount).
  const homeDepartments = (pool && pool.homeDepartments) || [];

  // Actual number of distinct home departments in the pool, derived from the
  // devices themselves. homeDepartments.length would say 5 for Alaris.
  const departmentCount = new Set(
    devices.map((d) => d.home_department || 'Unassigned')
  ).size;

  const matchesFilter = (d) => {
    if (statusFilter === 'missing') return !d.found;
    if (statusFilter === 'drifted') return d.drifted;
    if (statusFilter === 'found') return d.found;
    return true;
  };

  // Pinned departments first (in pool order), then anything else.
  // Groups are built from the FILTERED device list, so a department with
  // nothing left to find drops out of the list entirely under "Missing".
  const groupedDevices = () => {
    const groups = new Map();
    homeDepartments.forEach((d) => groups.set(d, []));
    devices.filter(matchesFilter).forEach((d) => {
      const key = d.home_department || 'Unassigned';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    });
    return Array.from(groups.entries()).filter(([, list]) => list.length > 0);
  };

  // Unfiltered per-department totals, so a collapsed header can read
  // "12/60" rather than "12/12" while the Missing filter is on. Built once
  // per render rather than per group.
  const departmentTotals = (() => {
    const totals = new Map();
    devices.forEach((d) => {
      const key = d.home_department || 'Unassigned';
      const t = totals.get(key) || { found: 0, total: 0 };
      t.total += 1;
      if (d.found) t.found += 1;
      totals.set(key, t);
    });
    return totals;
  })();

  const toggleGroup = (dept) =>
    setCollapsed((prev) => ({ ...prev, [dept]: !prev[dept] }));

  // Naming cadence differs per pool (SCD is quarterly, Alaris runs with the
  // February and October PM weeks) and the server does not carry a cadence
  // field, so the example text lives here. Unknown pools fall back to the
  // label, which is always sensible if slightly generic.
  const NAME_EXAMPLES = {
    'scd-acs900': 'Q3 2026 SCD sweep',
    'alaris-iv-pumps': 'Feb 2027 Alaris sweep',
  };
  const namePlaceholderExample =
    NAME_EXAMPLES[poolKey] || (pool ? `${pool.label} sweep` : 'Q3 2026 sweep');

  const visibleGroups = groupedDevices();

  const filterChips = [
    { key: 'all', label: 'All', count: counters.total },
    { key: 'missing', label: 'Missing', count: counters.missing },
    { key: 'drifted', label: 'Drifted', count: counters.drifted },
    { key: 'found', label: 'Found', count: counters.found },
  ];

  const manualMatches = () => {
    const q = manualQuery.trim().toLowerCase();
    if (q.length < 1) return [];
    return devices
      .filter(
        (d) =>
          String(d.name || '').toLowerCase().includes(q) ||
          String(d.asset || '').toLowerCase().includes(q) ||
          String(d.serial || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  };

  const statusPill = (d) => {
    if (!d.found) {
      return { text: 'Not found', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
    }
    if (d.drifted) {
      return { text: `Found in ${d.found_at_department}`, bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
    }
    return { text: 'Found at home', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' };
  };

  if (loading && tab === 'pools') {
    return (
      <div className="content-card" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#6b7280' }}>
        <Loader size={18} className="roundup-spin" /> Loading roundup...
      </div>
    );
  }

  return (
    <div className="content-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ClipboardList size={20} color="white" />
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#111827' }}>Equipment Roundup</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{pool ? pool.label : 'Loading pool...'}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', padding: '0 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
        {[
          { key: 'pools', label: 'Pools', icon: ClipboardList },
          { key: 'history', label: 'History', icon: History },
        ].map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '0.7rem 0.9rem', fontSize: '0.9rem',
                fontWeight: active ? 600 : 400,
                color: active ? '#2563eb' : '#6b7280',
                borderBottom: '2px solid ' + (active ? '#2563eb' : 'transparent'),
                marginBottom: -1,
              }}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: '1.25rem', display: tab === 'pools' ? 'block' : 'none' }}>
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Pool picker. Rendered in both states, not just on the landing
            screen: with a roundup open for one pool it is the only way back
            to the other. Hidden when there is only one pool, which keeps the
            single-pool screen exactly as it was. */}
        {pools.length > 1 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
              Equipment pool
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {pools.map((p) => (
                <button
                  key={p.key}
                  onClick={() => { setPoolKey(p.key); setStatusFilter('all'); }}
                  style={{
                    border: '1px solid ' + (poolKey === p.key ? '#2563eb' : '#d1d5db'),
                    background: poolKey === p.key ? '#2563eb' : '#fff',
                    color: poolKey === p.key ? '#fff' : '#374151',
                    borderRadius: 999, padding: '0.5rem 0.95rem', fontSize: '0.88rem',
                    fontWeight: poolKey === p.key ? 600 : 400, cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Landing: no roundup in progress ---------- */}
        {!roundup && (
          <div style={{ maxWidth: 520 }}>
            <div style={{ fontSize: '0.95rem', color: '#374151', marginBottom: '0.75rem' }}>
              No roundup is in progress. This pool has <strong>{devices.length}</strong> devices across{' '}
              {departmentCount} home department{departmentCount === 1 ? '' : 's'}.
            </div>
            <input
              value={newName}
              onChange={(e) => { setNewName(e.target.value); if (nameError) setNameError(''); }}
              placeholder={`Roundup name — e.g. ${namePlaceholderExample}`}
              style={{ width: '100%', border: '1px solid ' + (nameError ? '#fca5a5' : '#d1d5db'), borderRadius: 8, padding: '0.6rem 0.85rem', fontSize: '0.95rem', marginBottom: nameError ? '0.4rem' : '0.75rem' }}
            />
            {nameError && (
              <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                {nameError}
              </div>
            )}
            <button
              onClick={startRoundup}
              disabled={starting}
              style={{ background: starting ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem 1.2rem', fontSize: '0.95rem', fontWeight: 500, cursor: starting ? 'default' : 'pointer' }}
            >
              {starting ? 'Starting...' : 'Start New Roundup'}
            </button>
          </div>
        )}

        {/* ---------- Active roundup ---------- */}
        {roundup && (
          <>
            {/* Progress: the one number that matters mid-walk. */}
            <div style={{ marginBottom: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                  {counters.found} of {counters.total} found
                </span>
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {counters.total > 0 ? Math.round((counters.found / counters.total) * 100) : 0}%
                </span>
              </div>
              <div style={{ height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${counters.total > 0 ? (counters.found / counters.total) * 100 : 0}%`,
                    background: '#047857',
                    borderRadius: 999,
                    transition: 'width 200ms ease',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Found', value: `${counters.found} of ${counters.total}`, color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' },
                { label: 'Drifted', value: counters.drifted, color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
                { label: 'Still missing', value: counters.missing, color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
              ].map((c) => (
                <div key={c.label} style={{ flex: '1 1 140px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '0.75rem 1rem' }}>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: c.color, fontWeight: 600 }}>{c.label}</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827' }}>{c.value}</div>
                </div>
              ))}
            </div>

            {pending.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '0.7rem 1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
                <AlertTriangle size={18} />
                <div style={{ flex: 1 }}>
                  {pending.length} scan{pending.length === 1 ? '' : 's'} not yet saved to the server (no signal?).
                </div>
                <button
                  onClick={retryPending}
                  disabled={retrying}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#b45309', color: 'white', border: 'none', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', cursor: retrying ? 'default' : 'pointer' }}
                >
                  <RefreshCw size={14} className={retrying ? 'roundup-spin' : undefined} />
                  {retrying ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                onClick={() => { setScanMessage(''); setScannerOpen(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem 1.2rem', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer' }}
              >
                <Camera size={18} /> Scan Device
              </button>
              <button
                onClick={loadCurrent}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.7rem 1rem', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                <RefreshCw size={15} /> Refresh
              </button>
            </div>

            {scanMessage && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '0.7rem 1rem', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                {scanMessage}
              </div>
            )}

            {/* Manual entry fallback */}
            <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.5rem 0.75rem', background: '#fff' }}>
                <Search size={16} color="#6b7280" />
                <input
                  value={manualQuery}
                  onChange={(e) => setManualQuery(e.target.value)}
                  placeholder="Enter asset # manually"
                  inputMode="numeric"
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.95rem' }}
                />
                {manualQuery && (
                  <button onClick={() => setManualQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
                    <X size={16} />
                  </button>
                )}
              </div>
              {manualMatches().length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  {manualMatches().map((d) => (
                    <button
                      key={d.inventory_id}
                      onClick={() => { openSaveModal(d); setManualQuery(''); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', padding: '0.6rem 0.85rem', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      <strong>{d.name}</strong>
                      <span style={{ color: '#6b7280' }}> · {d.asset} · {d.home_department || 'no home dept'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status filter — the "Missing" view is the one that matters
                once a large pool is half walked. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
              {filterChips.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setStatusFilter(c.key)}
                  style={{
                    border: '1px solid ' + (statusFilter === c.key ? '#2563eb' : '#d1d5db'),
                    background: statusFilter === c.key ? '#2563eb' : '#fff',
                    color: statusFilter === c.key ? '#fff' : '#374151',
                    borderRadius: 999, padding: '0.4rem 0.8rem', fontSize: '0.83rem',
                    fontWeight: statusFilter === c.key ? 600 : 400, cursor: 'pointer',
                  }}
                >
                  {c.label} ({c.count})
                </button>
              ))}
            </div>

            {/* Device list grouped by home department */}
            {visibleGroups.length === 0 && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.25rem', textAlign: 'center' }}>
                {statusFilter === 'missing'
                  ? 'Nothing left to find — every device in this pool has been logged.'
                  : 'No devices match this filter.'}
              </div>
            )}
            {visibleGroups.map(([dept, list]) => {
              const totals = departmentTotals.get(dept) || { found: 0, total: list.length };
              const isCollapsed = !!collapsed[dept];
              return (
              <div key={dept} style={{ marginBottom: '1.25rem' }}>
                <button
                  onClick={() => toggleGroup(dept)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, color: '#374151', fontSize: '0.9rem', marginBottom: '0.5rem', background: 'none', border: 'none', padding: '0.2rem 0', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                >
                  {isCollapsed ? <ChevronRight size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
                  <MapPin size={15} color="#6b7280" />
                  {dept}
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                    ({totals.found}/{totals.total}
                    {statusFilter !== 'all' ? ` · ${list.length} shown` : ''})
                  </span>
                </button>
                <div style={{ display: isCollapsed ? 'none' : 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {list.map((d) => {
                    const pill = statusPill(d);
                    return (
                      <button
                        key={d.inventory_id}
                        onClick={() => openSaveModal(d)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.65rem 0.9rem', background: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.92rem' }}>
                            {d.name}
                            {d.found && !d.drifted && <CheckCircle size={14} color="#047857" style={{ marginLeft: 6, verticalAlign: 'text-bottom' }} />}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.asset}{d.serial ? ` · S/N ${d.serial}` : ''}
                          </div>
                        </div>
                        <span style={{ flexShrink: 0, background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`, borderRadius: 999, padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
                          {pill.text}{d.pendingSave ? ' (queued)' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              );
            })}

            <button
              onClick={() => setCompleteOpen(true)}
              style={{ width: '100%', background: '#111827', color: 'white', border: 'none', borderRadius: 8, padding: '0.85rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Complete Roundup
            </button>
          </>
        )}
      </div>

      {/* ---------- History tab ---------- */}
      {tab === 'history' && (
        <div style={{ padding: '1.25rem' }}>
          {historyError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
              {historyError}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Pool</span>
            <select
              value={historyPool}
              onChange={(e) => setHistoryPool(e.target.value)}
              style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '0.45rem 0.6rem', fontSize: '0.9rem', background: '#fff' }}
            >
              <option value="">All pools</option>
              {pools.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={() => loadHistory(historyPool)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.45rem 0.8rem', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {historyLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#6b7280', fontSize: '0.9rem' }}>
              <Loader size={16} className="roundup-spin" /> Loading history...
            </div>
          )}

          {!historyLoading && history.length === 0 && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.25rem', fontSize: '0.9rem', color: '#6b7280', textAlign: 'center' }}>
              No roundups yet for this filter.
            </div>
          )}

          {!historyLoading && history.map((row) => {
            const c = complianceColor(row.compliance_pct);
            return (
              <div key={row.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '0.6rem', background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
                      {displayName(row)}
                      {row.in_progress && (
                        <span style={{ marginLeft: 8, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0.1rem 0.5rem', fontSize: '0.7rem', fontWeight: 600 }}>
                          In progress
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 3 }}>
                      {row.pool_label} · {formatDate(row.started_at)}
                      {row.ended_at ? ` – ${formatDate(row.ended_at)}` : ''}
                      {' · '}
                      {row.in_progress ? 'ongoing' : formatDuration(row.started_at, row.ended_at)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>
                      {row.at_home} of {row.total} at home · {row.counters.drifted} drifted · {row.counters.missing} missing
                    </div>
                  </div>

                  <span style={{ flexShrink: 0, background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 999, padding: '0.3rem 0.7rem', fontSize: '0.9rem', fontWeight: 700, alignSelf: 'center' }}>
                    {row.compliance_pct}%
                  </span>

                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignSelf: 'center' }}>
                    <button
                      onClick={() => openDetail(row)}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.45rem 0.75rem', fontSize: '0.83rem', cursor: 'pointer' }}
                    >
                      <Eye size={14} /> View
                    </button>
                    <button
                      onClick={() => deleteRoundup(row)}
                      disabled={deletingId === row.id}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 8, padding: '0.45rem 0.75rem', fontSize: '0.83rem', cursor: deletingId === row.id ? 'default' : 'pointer' }}
                    >
                      <Trash2 size={14} /> {deletingId === row.id ? '...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- Roundup detail ---------- */}
      {detail && (
        <div
          onClick={() => setDetail(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#111827' }}>{displayName(detail.roundup)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>
                  {detail.roundup.pool_label} · {formatDate(detail.roundup.started_at)}
                  {detail.roundup.ended_at ? ` – ${formatDate(detail.roundup.ended_at)}` : ' · ongoing'}
                </div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1.25rem' }}>
              {detailLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#6b7280', fontSize: '0.9rem' }}>
                  <Loader size={16} className="roundup-spin" /> Loading breakdown...
                </div>
              )}

              {!detailLoading && detail.error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem' }}>
                  Could not load the breakdown: {detail.error}
                </div>
              )}

              {!detailLoading && detail.departments && detail.departments.length === 0 && !detail.error && (
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  This pool has no devices, so there is nothing to break down.
                </div>
              )}

              {!detailLoading && detail.departments && detail.departments.length > 0 && (
                <>
                  <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.75rem' }}>
                    <strong>{detail.at_home}</strong> of <strong>{detail.total}</strong> found at home
                    {' · '}<strong>{detail.compliance_pct}%</strong> compliance
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          {['Department', 'Home', 'At home', 'Drifted', 'Missing', '%'].map((h, i) => (
                            <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '0.5rem 0.6rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.departments.map((d) => (
                          <tr key={d.department}>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', color: '#111827', whiteSpace: 'nowrap' }}>{d.department}</td>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: '#6b7280' }}>{d.home_count}</td>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: '#047857', fontWeight: 600 }}>{d.found_at_home}</td>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: d.drifted ? '#b45309' : '#9ca3af' }}>{d.drifted}</td>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: d.missing ? '#b91c1c' : '#9ca3af' }}>{d.missing}</td>
                            <td style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600, color: complianceColor(d.compliance_pct).color }}>{d.compliance_pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.75rem', lineHeight: 1.5 }}>
                    {detail.basis_source === 'snapshot' ? (
                      <>
                        <strong style={{ color: '#6b7280' }}>Numbers from completion snapshot.</strong>{' '}
                        The device list and home departments were frozen when this
                        roundup was completed, so these figures will not change if
                        inventory changes later.
                      </>
                    ) : (
                      <>
                        <strong style={{ color: '#6b7280' }}>Numbers recomputed from current inventory.</strong>{' '}
                        This roundup predates completion snapshots, so the device
                        total and each device's home department come from NatchInv
                        as it stands today — they can move if devices are retired
                        or home departments are re-seeded.
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Scanner ---------- */}
      {scannerOpen && (
        <Suspense
          fallback={
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', gap: '0.6rem' }}>
              <Loader size={20} className="roundup-spin" /> Loading scanner...
            </div>
          }
        >
          <RoundupScanner onDetected={handleDetected} onClose={() => setScannerOpen(false)} />
        </Suspense>
      )}

      {/* ---------- Found-in modal ---------- */}
      {modalDevice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: '1.05rem' }}>{modalDevice.name}</div>
              <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: 2 }}>
                {modalDevice.asset} · home: {modalDevice.home_department || 'unassigned'}
              </div>
            </div>

            <div style={{ padding: '1.25rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Found in</div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
                {homeDepartments.map((dep) => (
                  <button
                    key={dep}
                    onClick={() => { setModalDept(dep); setModalOtherDept(''); }}
                    style={{
                      border: '1px solid ' + (modalDept === dep ? '#2563eb' : '#d1d5db'),
                      background: modalDept === dep ? '#2563eb' : '#fff',
                      color: modalDept === dep ? '#fff' : '#374151',
                      borderRadius: 999, padding: '0.45rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer',
                    }}
                  >
                    {dep}
                  </button>
                ))}
              </div>

              <select
                value={homeDepartments.includes(modalDept) ? '' : modalDept}
                onChange={(e) => { setModalDept(e.target.value); setModalOtherDept(''); }}
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem 0.7rem', fontSize: '0.9rem', marginBottom: '0.75rem', background: '#fff' }}
              >
                <option value="">Somewhere else...</option>
                {departments
                  .filter((dep) => !homeDepartments.includes(dep))
                  .map((dep) => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                <option value="__other__">Other (type it in)</option>
              </select>

              {modalDept === '__other__' && (
                <input
                  value={modalOtherDept}
                  onChange={(e) => setModalOtherDept(e.target.value)}
                  placeholder="Where was it?"
                  maxLength={100}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem 0.7rem', fontSize: '0.9rem', marginBottom: '0.75rem' }}
                />
              )}

              <textarea
                value={modalNotes}
                onChange={(e) => setModalNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                maxLength={500}
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem 0.7rem', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' }}
              />

              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Returned to <strong>{modalDevice.home_department || 'its home department'}</strong> unless noted otherwise.
              </div>

              {modalError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                  {modalError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1.25rem 1.25rem' }}>
              <button
                onClick={() => setModalDevice(null)}
                style={{ flex: 1, border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.7rem', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={saveMovement}
                disabled={saving}
                style={{ flex: 2, background: saving ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem', fontSize: '0.9rem', fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Complete confirmation ---------- */}
      {completeOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440 }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#111827' }}>
              Complete this roundup?
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ fontSize: '0.92rem', color: '#374151', lineHeight: 1.6 }}>
                <div><strong>{counters.found}</strong> of <strong>{counters.total}</strong> devices found</div>
                <div><strong>{counters.drifted}</strong> found outside their home department</div>
                <div><strong>{counters.missing}</strong> still missing</div>
              </div>

              {pending.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                  {pending.length} scan{pending.length === 1 ? '' : 's'} still unsaved. Retry them before completing, or they will be lost.
                </div>
              )}

              <textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="Notes for this roundup (optional)"
                rows={3}
                maxLength={500}
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem 0.7rem', fontSize: '0.9rem', fontFamily: 'inherit', marginTop: '0.75rem', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1.25rem 1.25rem' }}>
              <button
                onClick={() => setCompleteOpen(false)}
                style={{ flex: 1, border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.7rem', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Not yet
              </button>
              <button
                onClick={completeRoundup}
                disabled={completing}
                style={{ flex: 2, background: completing ? '#9ca3af' : '#111827', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem', fontSize: '0.9rem', fontWeight: 600, cursor: completing ? 'default' : 'pointer' }}
              >
                {completing ? 'Completing...' : 'Complete Roundup'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.roundup-spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};

export default RoundupTracker;
